import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';

/**
 * Configuration for a store, including currency, aliasing, and product availability
 */
export interface StoreConfig {
  store: ShopifyStore;
  currency: Currency;
  baseStore?: ShopifyStore; // Only set if this store is aliased to another

  /**
   * Only specify products that are NOT supported.
   * If a product key is missing or undefined, it's assumed to be supported.
   * This opt-out approach keeps the config clean and concise.
   */
  unsupportedProducts?: {
    ring?: boolean; // Set to true if Ring Air is NOT supported
    ring_pro?: boolean; // Set to true if Ring PRO is NOT supported
    m1?: boolean; // Set to true if M1 CGM is NOT supported
    home?: boolean; // Set to true if Home device is NOT supported
    rare?: boolean; // Set to true if Rare editions are NOT supported
    shop?: boolean; // Set to true if Shop/merch is NOT supported
    x?: boolean; // Set to true if Ultrahuman X is NOT supported
  };

  /**
   * Special M1 configuration for EU countries with Multi-Country Fulfillment
   */
  m1Config?: {
    mcf: boolean; // Multi-Country Fulfillment (special EU handling)
  };
}

/**
 * Centralized store configuration map for all countries.
 * Single source of truth for country → store → currency mappings.
 */
const COUNTRY_STORE_CONFIG: Record<string, StoreConfig> = {
  // India
  IN: { store: ShopifyStore.IN, currency: Currency.inr },

  // United States (Ring Air, Diesel Ring, Rare Ring not currently sold)
  US: {
    store: ShopifyStore.US,
    currency: Currency.usd,
    unsupportedProducts: { ring: true, rare: true },
  },

  // Puerto Rico (US territory - treated identically to US)
  PR: {
    store: ShopifyStore.US,
    currency: Currency.usd,
    unsupportedProducts: { ring: true, rare: true },
  },

  // Canada (aliased to US for product variants)
  CA: {
    store: ShopifyStore.CA,
    currency: Currency.cad,
    baseStore: ShopifyStore.US,
  },

  // United Kingdom (aliased to EU for product variants, MCF for M1)
  GB: {
    store: ShopifyStore.GB,
    currency: Currency.gbp,
    baseStore: ShopifyStore.EU,
    m1Config: { mcf: true },
  },

  // Australia (aliased to EU for product variants)
  AU: {
    store: ShopifyStore.AU,
    currency: Currency.aud,
    baseStore: ShopifyStore.EU,
  },

  // European Union countries (MCF = Multi-Country Fulfillment for M1)
  AT: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  BE: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  BG: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  HR: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  CY: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  CZ: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  DK: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  EE: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  FI: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  FR: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  DE: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  GR: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  HU: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  IE: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  IT: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  LV: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  LT: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  LU: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  MT: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  NL: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  PL: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  PT: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  RO: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  SK: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  SI: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  ES: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },
  SE: {
    store: ShopifyStore.EU,
    currency: Currency.eur,
    m1Config: { mcf: true },
  },

  // Non-EU European countries with M1 support
  CH: {
    store: ShopifyStore.ROW,
    currency: Currency.usd,
    m1Config: { mcf: false },
  },
  IS: {
    store: ShopifyStore.ROW,
    currency: Currency.usd,
    m1Config: { mcf: false },
  },
  NO: {
    store: ShopifyStore.ROW,
    currency: Currency.usd,
    m1Config: { mcf: false },
  },
  MC: {
    store: ShopifyStore.ROW,
    currency: Currency.usd,
    m1Config: { mcf: true },
  },

  // United Arab Emirates & GCC countries
  AE: { store: ShopifyStore.AE, currency: Currency.aed },
  // Saudi Arabia (NEW - using AE store with SAR currency)
  // Ring supported, M1 not yet available
  SA: {
    store: ShopifyStore.SA,
    baseStore: ShopifyStore.EU,
    currency: Currency.sar,
  },
  MX: {
    store: ShopifyStore.MX,
    baseStore: ShopifyStore.EU,
    currency: Currency.mxn,
  },
  // South Africa (aliased to EU for product variants)
  ZA: {
    store: ShopifyStore.ZA,
    baseStore: ShopifyStore.EU,
    currency: Currency.zar,
  },
  QA: { store: ShopifyStore.AE, currency: Currency.aed },
  BH: { store: ShopifyStore.AE, currency: Currency.aed },
  KW: { store: ShopifyStore.AE, currency: Currency.aed },
  OM: { store: ShopifyStore.AE, currency: Currency.aed },

  // Japan (aliased to EU for product variants; has Japanese language support)
  JP: {
    store: ShopifyStore.JP,
    baseStore: ShopifyStore.EU,
    currency: Currency.jpy,
  },
  // Thailand (uses ROW store; has Thai language support)
  TH: { store: ShopifyStore.ROW, currency: Currency.usd },

  // Non-EU European countries (EU store)
  RS: { store: ShopifyStore.EU, currency: Currency.eur },
  JE: { store: ShopifyStore.EU, currency: Currency.eur },
  AD: { store: ShopifyStore.EU, currency: Currency.eur },
  AL: { store: ShopifyStore.EU, currency: Currency.eur },

  LI: { store: ShopifyStore.EU, currency: Currency.eur },
  BA: { store: ShopifyStore.EU, currency: Currency.eur },
  MK: { store: ShopifyStore.EU, currency: Currency.eur },
  MD: { store: ShopifyStore.EU, currency: Currency.eur },
  XK: { store: ShopifyStore.EU, currency: Currency.eur },
  ME: { store: ShopifyStore.EU, currency: Currency.eur },
  SJ: { store: ShopifyStore.EU, currency: Currency.eur },
  GI: { store: ShopifyStore.EU, currency: Currency.eur },
  IM: { store: ShopifyStore.EU, currency: Currency.eur },
  GG: { store: ShopifyStore.EU, currency: Currency.eur },
  SM: { store: ShopifyStore.EU, currency: Currency.eur },

  // Ukraine (ROW store)
  UA: { store: ShopifyStore.ROW, currency: Currency.usd },

  // ROW countries — Asia Pacific
  SG: { store: ShopifyStore.ROW, currency: Currency.usd },
  PH: { store: ShopifyStore.ROW, currency: Currency.usd },
  MY: { store: ShopifyStore.ROW, currency: Currency.usd },
  TW: { store: ShopifyStore.ROW, currency: Currency.usd },
  ID: { store: ShopifyStore.ROW, currency: Currency.usd },
  VN: { store: ShopifyStore.ROW, currency: Currency.usd },
  KR: { store: ShopifyStore.ROW, currency: Currency.usd },
  CN: { store: ShopifyStore.ROW, currency: Currency.usd },
  HK: { store: ShopifyStore.ROW, currency: Currency.usd },
  MO: { store: ShopifyStore.ROW, currency: Currency.usd },
  MN: { store: ShopifyStore.ROW, currency: Currency.usd },
  KH: { store: ShopifyStore.ROW, currency: Currency.usd },
  BN: { store: ShopifyStore.ROW, currency: Currency.usd },
  TL: { store: ShopifyStore.ROW, currency: Currency.usd },
  NZ: { store: ShopifyStore.ROW, currency: Currency.usd },
  FJ: { store: ShopifyStore.ROW, currency: Currency.usd },
  CK: { store: ShopifyStore.ROW, currency: Currency.usd },
  PF: { store: ShopifyStore.ROW, currency: Currency.usd },

  // ROW countries — South & Central Asia
  LK: { store: ShopifyStore.ROW, currency: Currency.usd },
  MV: { store: ShopifyStore.ROW, currency: Currency.usd },
  NP: { store: ShopifyStore.ROW, currency: Currency.usd },
  BD: { store: ShopifyStore.ROW, currency: Currency.usd },
  KZ: { store: ShopifyStore.ROW, currency: Currency.usd },
  UZ: { store: ShopifyStore.ROW, currency: Currency.usd },
  KG: { store: ShopifyStore.ROW, currency: Currency.usd },
  AM: { store: ShopifyStore.ROW, currency: Currency.usd },
  AZ: { store: ShopifyStore.ROW, currency: Currency.usd },
  GE: { store: ShopifyStore.ROW, currency: Currency.usd },

  // ROW countries — Middle East (non-AE store)
  IL: { store: ShopifyStore.ROW, currency: Currency.usd },
  TR: { store: ShopifyStore.ROW, currency: Currency.usd },
  JO: { store: ShopifyStore.ROW, currency: Currency.usd },

  LB: { store: ShopifyStore.ROW, currency: Currency.usd },

  // ROW countries — Americas
  CO: { store: ShopifyStore.ROW, currency: Currency.usd },
  BR: { store: ShopifyStore.ROW, currency: Currency.usd },
  CL: { store: ShopifyStore.ROW, currency: Currency.usd },
  EC: { store: ShopifyStore.ROW, currency: Currency.usd },
  AR: { store: ShopifyStore.ROW, currency: Currency.usd },
  PE: { store: ShopifyStore.ROW, currency: Currency.usd },
  VE: { store: ShopifyStore.ROW, currency: Currency.usd },
  PY: { store: ShopifyStore.ROW, currency: Currency.usd },
  GY: { store: ShopifyStore.ROW, currency: Currency.usd },
  SR: { store: ShopifyStore.ROW, currency: Currency.usd },
  BO: { store: ShopifyStore.ROW, currency: Currency.usd },
  UY: { store: ShopifyStore.ROW, currency: Currency.usd },
  GF: { store: ShopifyStore.ROW, currency: Currency.usd },
  GT: { store: ShopifyStore.ROW, currency: Currency.usd },
  HN: { store: ShopifyStore.ROW, currency: Currency.usd },
  SV: { store: ShopifyStore.ROW, currency: Currency.usd },
  NI: { store: ShopifyStore.ROW, currency: Currency.usd },
  CR: { store: ShopifyStore.ROW, currency: Currency.usd },
  PA: { store: ShopifyStore.ROW, currency: Currency.usd },
  DO: { store: ShopifyStore.ROW, currency: Currency.usd },
  TT: { store: ShopifyStore.ROW, currency: Currency.usd },
  BS: { store: ShopifyStore.ROW, currency: Currency.usd },
  BB: { store: ShopifyStore.ROW, currency: Currency.usd },
  JM: { store: ShopifyStore.ROW, currency: Currency.usd },
  BM: { store: ShopifyStore.ROW, currency: Currency.usd },
  KY: { store: ShopifyStore.ROW, currency: Currency.usd },
  AW: { store: ShopifyStore.ROW, currency: Currency.usd },
  GP: { store: ShopifyStore.ROW, currency: Currency.usd },
  MQ: { store: ShopifyStore.ROW, currency: Currency.usd },
  TC: { store: ShopifyStore.ROW, currency: Currency.usd },
  CW: { store: ShopifyStore.ROW, currency: Currency.usd },
  MF: { store: ShopifyStore.ROW, currency: Currency.usd },
  BL: { store: ShopifyStore.ROW, currency: Currency.usd },
  SX: { store: ShopifyStore.ROW, currency: Currency.usd },
  GD: { store: ShopifyStore.ROW, currency: Currency.usd },
  GL: { store: ShopifyStore.ROW, currency: Currency.usd },

  // ROW countries — Africa
  NG: { store: ShopifyStore.ROW, currency: Currency.usd },
  KE: { store: ShopifyStore.ROW, currency: Currency.usd },
  GH: { store: ShopifyStore.ROW, currency: Currency.usd },
  EG: { store: ShopifyStore.ROW, currency: Currency.usd },
  MA: { store: ShopifyStore.ROW, currency: Currency.usd },
  MU: { store: ShopifyStore.ROW, currency: Currency.usd },
  CI: { store: ShopifyStore.ROW, currency: Currency.usd },
  SN: { store: ShopifyStore.ROW, currency: Currency.usd },
  AO: { store: ShopifyStore.ROW, currency: Currency.usd },
  BW: { store: ShopifyStore.ROW, currency: Currency.usd },
  NA: { store: ShopifyStore.ROW, currency: Currency.usd },
  TN: { store: ShopifyStore.ROW, currency: Currency.usd },
  SC: { store: ShopifyStore.ROW, currency: Currency.usd },
  ZM: { store: ShopifyStore.ROW, currency: Currency.usd },
  ZW: { store: ShopifyStore.ROW, currency: Currency.usd },
  RW: { store: ShopifyStore.ROW, currency: Currency.usd },
  BJ: { store: ShopifyStore.ROW, currency: Currency.usd },
  GN: { store: ShopifyStore.ROW, currency: Currency.usd },

  // ROW countries — Other territories
  RE: { store: ShopifyStore.ROW, currency: Currency.usd },
  YT: { store: ShopifyStore.ROW, currency: Currency.usd },
  TF: { store: ShopifyStore.ROW, currency: Currency.usd },
  AX: { store: ShopifyStore.ROW, currency: Currency.usd },

  // Rest of World (default)
  ROW: { store: ShopifyStore.ROW, currency: Currency.usd },
};

/**
 * Get complete store configuration for a country code.
 * This is the preferred method for new code as it provides all related configuration.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB', 'SA')
 * @returns Complete store configuration including store, currency, and base store (if aliased)
 *
 * @example
 * const config = getStoreConfig('MX');
 * // Returns: { store: ShopifyStore.US, currency: Currency.mxn, baseStore: ShopifyStore.US }
 *
 * // To get the currency symbol, use getCurrencySymbolFromEnum:
 * const symbol = getCurrencySymbolFromEnum(config.currency); // '$'
 */
export const getStoreConfig = (countryCode: string): StoreConfig => {
  const code = countryCode.toUpperCase();

  // Special case: 'global' route maps to ROW
  if (code === 'GLOBAL') {
    return COUNTRY_STORE_CONFIG['ROW'];
  }

  // Return config from map, fallback to ROW for unknown countries
  return COUNTRY_STORE_CONFIG[code] || COUNTRY_STORE_CONFIG['ROW'];
};

export { COUNTRY_STORE_CONFIG };
